import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { renderMarkdown } from '../../timeline/utils/markdownRenderer';
import { formatYear } from '../../timeline/utils/dateUtils';
import TimelineFirestoreService from '../../timeline/services/TimelineFirestoreService';
import '../styles/MarkerDetailPanel.css';

const timelineService = new TimelineFirestoreService();

/**
 * MarkerDetailPanel - Shows details of a selected map marker.
 * Resolves linked event IDs to titles/dates and provides navigation links.
 */
function MarkerDetailPanel({ marker, onEdit, onDelete, onClose }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [resolvedEvents, setResolvedEvents] = useState([]);
  const navigate = useNavigate();

  // Resolve linked event IDs to full event objects
  useEffect(() => {
    if (marker && marker.linkedEvents && marker.linkedEvents.length > 0) {
      Promise.all(
        marker.linkedEvents.map(id => timelineService.getEvent(id))
      ).then(events => {
        setResolvedEvents(events.filter(Boolean));
      }).catch(() => setResolvedEvents([]));
    } else {
      setResolvedEvents([]);
    }
  }, [marker]);

  if (!marker) return null;

  const handleEventClick = (event) => {
    // Navigate to timeline with the event ID as a query param to highlight it
    navigate(`/timeline?event=${event.id}`);
  };

  return (
    <div className="marker-detail-panel">
      <div className="mdp-header">
        <h2 className="mdp-title">{marker.title}</h2>
        <button className="mdp-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="mdp-body">
        <div className="mdp-coords">
          {marker.lat.toFixed(4)}, {marker.lng.toFixed(4)}
        </div>

        {marker.summary && <p className="mdp-summary">{marker.summary}</p>}

        {marker.category && (
          <span className="mdp-category">{marker.category.replace(/_/g, ' ')}</span>
        )}

        {marker.notes && (
          <div className="mdp-section">
            <h3>Notes</h3>
            <div className="mdp-notes" dangerouslySetInnerHTML={{ __html: renderMarkdown(marker.notes) }} />
          </div>
        )}

        {marker.tags && marker.tags.length > 0 && (
          <div className="mdp-section">
            <h3>Tags</h3>
            <div className="mdp-tags">
              {marker.tags.map((tag, i) => <span key={i} className="mdp-tag">{tag}</span>)}
            </div>
          </div>
        )}

        {resolvedEvents.length > 0 && (
          <div className="mdp-section">
            <h3>Linked Timeline Events</h3>
            <ul className="mdp-linked-events">
              {resolvedEvents.map(event => (
                <li key={event.id}>
                  <button
                    className="mdp-event-link"
                    onClick={() => handleEventClick(event)}
                  >
                    <span className="mdp-event-title">{event.title}</span>
                    <span className="mdp-event-date">{formatYear(event.startYear)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mdp-actions">
        <button className="mdp-edit-btn" onClick={onEdit}>Edit</button>
        <button className="mdp-delete-btn" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
      </div>

      {showDeleteConfirm && (
        <div className="mdp-delete-overlay">
          <div className="mdp-delete-dialog">
            <p>Delete "{marker.title}"?</p>
            <div className="mdp-delete-actions">
              <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="mdp-delete-confirm" onClick={() => { setShowDeleteConfirm(false); onDelete(); }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarkerDetailPanel;
